import React, { useState, useEffect, useCallback } from 'react';
import { fetchUserAttributes, updateUserAttribute, confirmUserAttribute, sendUserAttributeVerificationCode, signOut, deleteUser } from 'aws-amplify/auth';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trash2, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';

export default function ProfilePage() {
  const [attributes, setAttributes] = useState({});
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState({ joined: false, loading: true });
  const navigate = useNavigate();

  const API_BASE = "https://7oduilb7tc.execute-api.us-east-1.amazonaws.com";
  const STAGE = (window.location.hostname === 'test.dopove.com' || window.location.hostname.includes('s3-website')) ? 'test' : 'prod';
  const API_URL = `${API_BASE}/${STAGE}/waitlist`;

  const loadUserAttributes = useCallback(async () => {
    setLoading(true);
    try {
      const attrs = await fetchUserAttributes();
      setAttributes(attrs);
    } catch (err) {
      console.error(err);
      setError('Failed to load user profile. Please sign in again.');
      if (err.name === 'NotAuthorizedException') {
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const checkWaitlistStatus = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_URL}?email=${email}`);
      const data = await response.json();
      setWaitlistStatus({ joined: data.joined, loading: false });
    } catch (err) {
      console.error("Waitlist check failed:", err);
      setWaitlistStatus({ joined: false, loading: false });
    }
  }, [API_URL]);

  useEffect(() => {
    loadUserAttributes();
  }, [loadUserAttributes]);

  useEffect(() => {
    if (attributes.email) {
      checkWaitlistStatus(attributes.email);
    } else if (!loading) {
      setWaitlistStatus(prev => ({ ...prev, loading: false }));
    }
  }, [attributes.email, loading, checkWaitlistStatus]);



  const handleJoinWaitlist = async () => {
    if (attributes.email_verified !== 'true') {
      setError("Email verification required. Please verify your email address to join the GoingMerry waitlist.");
      return;
    }

    setUpdateLoading(true);
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: attributes.email,
          name: attributes.name || attributes.username,
          interest: 'goingmerry'
        })
      });
      setWaitlistStatus({ joined: true, loading: false });
      setMessage("You've been added to the GoingMerry waitlist");
    } catch (err) {
      setError("Failed to join waitlist. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };



  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setUpdateLoading(true);
    try {
      // 1. Attempt to remove from Waitlist synchronously with account deletion
      if (attributes.email) {
        try {
          await fetch(`${API_URL}?email=${attributes.email}`, {
            method: 'DELETE'
          });
          console.log("Waitlist entry purged successfully");
        } catch (waitlistErr) {
          console.error("Non-blocking waitlist purge error:", waitlistErr);
        }
      }

      // 2. Perform global account deletion
      await deleteUser();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to delete account.');
      setShowDeleteConfirm(false);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateAttribute = async (name, value) => {
    if (name === 'email' && attributes.email_verified === 'true') {
      setError("Verified email addresses cannot be modified.");
      return;
    }
    
    let finalValue = value;
    
    // Auto-format phone to E.164 if needed (Cognito requirement)
    if (name === 'phone_number' && value) {
      const digits = value.replace(/\D/g, '');
      if (digits) {
        // If it starts with + just clean it, otherwise prepend + (assume user knows what they're doing)
        finalValue = value.trim().startsWith('+') ? `+${digits}` : `+${digits}`;
      }
    }

    setUpdateLoading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await updateUserAttribute({
        userAttribute: {
          attributeKey: name,
          value: finalValue
        }
      });
      
      if (result.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        setVerificationStep(name);
        setMessage(`A verification code has been sent to your ${name}`);
      } else {
        setMessage(`${name === 'email' ? 'Email' : 'Attribute'} updated successfully`);
        loadUserAttributes();
      }
    } catch (err) {
      setError(err.message || 'Failed to update attribute.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleVerifyExisting = async (attributeName) => {
    setUpdateLoading(true);
    setError(null);
    setMessage(null);
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: attributeName
      });
      setVerificationStep(attributeName);
      setMessage(`A verification code has been sent to your ${attributeName}`);
    } catch (err) {
      setError(err.message || 'Failed to send verification code.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleConfirmVerification = async () => {
    setUpdateLoading(true);
    setError(null);
    try {
      await confirmUserAttribute({
        userAttributeKey: verificationStep,
        confirmationCode: otpCode
      });
      setMessage('Verification successful');
      setVerificationStep(null);
      setOtpCode('');
      loadUserAttributes();
    } catch (err) {
      setError(err.message || 'Verification failed. Please check the code');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#191d2b] min-h-screen text-white flex flex-col items-center justify-center">
        <div className="animate-pulse text-2xl font-mono uppercase tracking-widest">Loading Profile...</div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Your profile" description="Manage your Dopove account." canonical="https://dopove.com/profile" noIndex />
      <div className="bg-[#191d2b] min-h-screen flex flex-col font-sans text-white">
      
      <main className="flex-grow py-20 px-6 max-w-2xl mx-auto w-full">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#23263a] border border-slate-700/50 rounded-3xl p-10 shadow-2xl relative overflow-hidden"
        >
          {/* Subtle Background Accent */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" />

          <div className="mb-10 text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="/full_logo_16x9.png" 
                alt="Dopove Logo" 
                className="w-full max-w-[280px] h-auto drop-shadow-lg"
              />
            </div>
            <h1 className="text-4xl font-black tracking-tighter uppercase mb-2">User Profile</h1>
            <p className="text-slate-400 font-medium">Node Security & Identity Management</p>
          </div>

          {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-center text-sm font-medium">{error}</div>}
          {message && <div className="mb-6 p-4 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-xl text-center text-sm font-medium">{message}</div>}

          <div className="space-y-10">
            {/* Name Section */}
            <div className="space-y-3">
              <label className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 ml-1">Account Identity</label>
              <div className="relative group">
                <input
                  type="text"
                  defaultValue={attributes.name || "Dopove Node"}
                  disabled={updateLoading}
                  className="w-full bg-[#191d2b] border border-slate-700/50 rounded-2xl px-5 py-4 focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 outline-none hover-lift focus-visible-ring transition-[border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] text-white font-bold group-hover:border-slate-600"
                  placeholder="Your Full Name"
                  onBlur={(e) => {
                    if (e.target.value !== attributes.name) {
                      handleUpdateAttribute('name', e.target.value);
                    }
                  }}
                />
              </div>
            </div>

            {/* Email Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center ml-1">
                <label className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">Global Identity</label>
                <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${attributes.email_verified === 'true' ? 'bg-teal-500/20 text-teal-400' : 'bg-orange-500/20 text-orange-400'}`}>
                  {attributes.email_verified === 'true' ? 'Verified' : 'Pending'}
                </div>
              </div>
              <div className="relative group">
                <input
                  type="email"
                  defaultValue={attributes.email}
                  disabled={updateLoading || !!verificationStep || attributes.email_verified === 'true'}
                  className="w-full bg-[#191d2b] border border-slate-700/50 rounded-2xl px-5 py-4 focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 outline-none hover-lift focus-visible-ring transition-[border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] text-white font-bold group-hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  placeholder="name@example.com"
                  onBlur={(e) => {
                    if (e.target.value !== attributes.email) {
                      handleUpdateAttribute('email', e.target.value);
                    }
                  }}
                />
                {attributes.email && attributes.email_verified !== 'true' && (
                  <button
                    onClick={() => handleVerifyExisting('email')}
                    disabled={updateLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] shadow-lg active:scale-95"
                  >
                    Verify Now
                  </button>
                )}
                {attributes.email_verified === 'true' && (
                  <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-slate-500">
                    <Lock className="w-3.5 h-3.5" />
                    <span className="text-[9px] font-black uppercase tracking-wider">Locked</span>
                  </div>
                )}
              </div>
            </div>

            {/* Phone Section */}
            <div className="space-y-3">
              <label className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 ml-1">Contact Connectivity</label>
              <div className="relative group">
                <input
                  type="tel"
                  defaultValue={attributes.phone_number}
                  disabled={updateLoading}
                  className="w-full bg-[#191d2b] border border-slate-700/50 rounded-2xl px-5 py-4 focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 outline-none hover-lift focus-visible-ring transition-[border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] text-white font-bold group-hover:border-slate-600"
                  placeholder="+1234567890"
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val !== attributes.phone_number) {
                      handleUpdateAttribute('phone_number', val);
                    }
                  }}
                />
              </div>
            </div>

            {/* OTP Verification Area */}
            <AnimatePresence>
              {verificationStep && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-8 bg-teal-500/5 border border-teal-500/20 rounded-[2rem] overflow-hidden"
                >
                  <h3 className="text-xl font-black mb-1 uppercase tracking-tight text-teal-400">Security Challenge</h3>
                  <p className="text-sm text-slate-400 mb-8">Verification code transmitted to your device</p>
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      className="bg-[#191d2b] border border-slate-700/50 rounded-2xl px-4 py-4 flex-grow text-center text-2xl tracking-[0.6em] font-black outline-none focus:border-teal-500/50 text-white"
                      maxLength={6}
                      placeholder="000000"
                    />
                    <button
                      onClick={handleConfirmVerification}
                      className="px-8 bg-gradient-to-r from-teal-500 to-blue-600 text-white font-black uppercase tracking-widest text-[10px] rounded-2xl hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] shadow-xl active:scale-95"
                    >
                      Verify
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>



            {/* Waitlist Section */}
            {!waitlistStatus.loading && (
              <div className="p-8 bg-gradient-to-br from-teal-500/10 to-blue-600/5 border border-teal-500/20 rounded-[2rem] relative group">
                {waitlistStatus.joined ? (
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-[9px] font-black uppercase tracking-widest mb-4">
                      <CheckCircle2 className="w-3 h-3" /> Waitlist Joined
                    </div>
                    <h3 className="text-xl font-black mb-2 uppercase tracking-tight text-white">You're already in the waitlist</h3>
                    <p className="text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                      Keep an eye on your email for updates!
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="text-center md:text-left">
                      <h3 className="text-xl font-black mb-1 uppercase tracking-tight text-white">Join the GoingMerry waitlist</h3>
                      <p className="text-sm text-slate-400 max-w-[280px]">Get notified when GoingMerry is available for local installation.</p>
                    </div>
                     <button
                       onClick={handleJoinWaitlist}
                       disabled={updateLoading}
                       className="whitespace-nowrap px-8 py-4 bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-2xl hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] shadow-xl active:scale-95"
                     >
                       Join waitlist
                     </button>
                  </div>
                )}
              </div>
            )}

            {/* Danger Zone / Global Actions */}
            <div className="mt-12 pt-10 border-t border-slate-700/50 space-y-6">
               <div className="flex flex-col gap-4">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center gap-3 py-4 bg-slate-800 hover:bg-slate-700 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>

                  <div className="p-1 px-1 bg-red-500/5 border border-red-500/10 rounded-[1.5rem] overflow-hidden">
                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full flex items-center justify-center gap-3 py-4 hover:bg-red-500/10 text-red-500/70 hover:text-red-500 font-black uppercase tracking-widest text-[11px] rounded-2xl hover-scale-press focus-visible-ring transition-[background-color,color,transform] duration-200 ease-[var(--ease-out)]"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Account
                      </button>
                    ) : (
                       <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-6 text-center"
                       >
                          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-4" />
                          <h4 className="text-red-500 font-black uppercase tracking-tighter text-lg mb-2">Final Confirmation</h4>
                          <p className="text-slate-500 text-xs mb-6 max-w-[240px] mx-auto leading-relaxed">Account deletion is permanent. All profile data will be purged.</p>
                          <div className="flex gap-4">
                             <button
                              onClick={() => setShowDeleteConfirm(false)}
                              className="flex-1 py-3 bg-slate-800 text-white font-bold rounded-xl text-[10px] uppercase tracking-widest hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]"
                             >
                               Abort
                             </button>
                             <button
                              onClick={handleDeleteAccount}
                              disabled={updateLoading}
                              className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl text-[10px] uppercase tracking-widest shadow-lg shadow-red-600/20 hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)]"
                             >
                               Delete
                             </button>
                          </div>
                       </motion.div>
                    )}
                  </div>
               </div>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
    </>
  );
}
